import { serializeParams } from "@/utils/apiUtils";
import { AxiosPromise } from "axios";
import { ApiRequestAdapter } from "../../common/api/ApiRequestAdapter";
import { apiService } from "../../common/api/apiService";
import { HttpContentType, HttpHeaders, HttpMethod } from "../../common/api/http";
import { PageSizes } from "../../common/constants";
import { ExportFileType, OrderDirection } from "../../common/enums";
import {
  EntityIdObject,
  EntityObject,
  OptimisticLockVersion,
  PageRequest,
  PageResult,
  TwoLevelEntityIdObject,
  TwoLevelEntityObject,
} from "../../common/types";
import { ContractOrderByColumn, ContractView } from "./enums";
import {
  ChangeContractVerificationStatus,
  Contract,
  ContractAttachment,
  ContractCommission,
  ContractExternalView,
  ContractFilterExportRequest,
  ContractFilterPageRequest,
  ContractFilterPageResult,
  ContractIncrement,
  ContractVehiclesExportRequest,
  CreateUpdateContract,
  CreateUpdateContractIncrement,
  UpdateContractAttachment,
  UpdateContractComplaintNote,
  UpdateContractEndDates,
  UpdateContractGainers,
} from "./types";

const FILTER_CONTRACTS = ApiRequestAdapter.create("/contracts/filter", HttpMethod.POST);
const IMPORT_CONTRACTS = ApiRequestAdapter.create("/contracts/import", HttpMethod.POST);
const DOWNLOAD_CONTRACTS_IMPORT_RESULT = ApiRequestAdapter.create("/contracts/import/{0}", HttpMethod.GET);
const DOWNLOAD_CONTRACTS_EXPORT = ApiRequestAdapter.create("/contracts/export", HttpMethod.POST);
const DOWNLOAD_CONTRACTS_VERIFICATION_EXPORT = ApiRequestAdapter.create(
  "/contracts/verification-export",
  HttpMethod.GET,
);
const DOWNLOAD_CONTRACT_VEHICLES_EXPORT = ApiRequestAdapter.create("/contracts/{0}/export-vehicles", HttpMethod.GET);

const GET_CONTRACT = ApiRequestAdapter.create("/contracts/{0}", HttpMethod.GET);
const GET_CONTRACT_EXTERNAL_VIEW = ApiRequestAdapter.create("/contracts/{0}/external-view", HttpMethod.GET);
const FILTER_CONTRACT_COMMISSIONS = ApiRequestAdapter.create("/contracts/{0}/commissions", HttpMethod.GET);
const CREATE_CONTRACT = ApiRequestAdapter.create("/contracts", HttpMethod.POST);
const CHANGE_CONTRACT_VERIFICATION_STATUS = ApiRequestAdapter.create(
  "/contracts/{0}/change-verification-status",
  HttpMethod.POST,
);
const TOGGLE_VERIFICATION_EXPORT_INCLUSION = ApiRequestAdapter.create(
  "/contracts/{0}/toggle-verification-export",
  HttpMethod.POST,
);
const UPDATE_CONTRACT = ApiRequestAdapter.create("/contracts/{0}", HttpMethod.PUT);
const UPDATE_CONTRACT_END_DATES = ApiRequestAdapter.create("/contracts/{0}/end-dates", HttpMethod.PUT);
const DELETE_CONTRACT = ApiRequestAdapter.create("/contracts/{0}", HttpMethod.DELETE);
const UPDATE_COMPLAINT_NOTE = ApiRequestAdapter.create("/contracts/{0}/complaint-note", HttpMethod.PUT);

const FILTER_CONTRACT_INCREMENTS = ApiRequestAdapter.create("/contracts/{0}/increments", HttpMethod.GET);
const CREATE_CONTRACT_INCREMENT = ApiRequestAdapter.create("/contracts/{0}/increments", HttpMethod.POST);
const UPDATE_CONTRACT_INCREMENT = ApiRequestAdapter.create("/contracts/{0}/increments/{1}", HttpMethod.PUT);
const DELETE_CONTRACT_INCREMENT = ApiRequestAdapter.create("/contracts/{0}/increments/{1}", HttpMethod.DELETE);

const UPLOAD_CONTRACT_ATTACHMENTS = ApiRequestAdapter.create("/contracts/{0}/attachments", HttpMethod.POST);
const UPDATE_CONTRACT_ATTACHMENT = ApiRequestAdapter.create("/contracts/{0}/attachments/{1}", HttpMethod.PATCH);
const DOWNLOAD_CONTRACT_ATTACHMENT = ApiRequestAdapter.create("/contracts/{0}/attachments/{1}", HttpMethod.GET);
const DOWNLOAD_CONTRACT_ATTACHMENTS_AS_ZIP = ApiRequestAdapter.create("/contracts/{0}/attachments/all", HttpMethod.GET);
const DELETE_CONTRACT_ATTACHMENT = ApiRequestAdapter.create("/contracts/{0}/attachments/{1}", HttpMethod.DELETE);

const DOWNLOAD_CONTRACT_COMMISSIONS_EXPORT = ApiRequestAdapter.create(
  "/contracts/{0}/commissions/export",
  HttpMethod.GET,
);

const UPDATE_CONTRACT_GAINERS = ApiRequestAdapter.create("/contracts/{0}/gainers", HttpMethod.PUT);

export const contractApi = {
  filterContracts: (request: ContractFilterPageRequest): AxiosPromise<ContractFilterPageResult> => {
    return apiService.post(FILTER_CONTRACTS.url, request, {
      params: { report: request.report },
    });
  },
  importContracts: (request: FormData): AxiosPromise<number> => {
    return apiService.post(IMPORT_CONTRACTS.url, request, {
      headers: { [HttpHeaders.CONTENT_TYPE]: HttpContentType.MULTIPART_FORM_DATA },
    });
  },
  downloadContractsImportResult: (request: EntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_CONTRACTS_IMPORT_RESULT.getParametrizedUrl(request.id), { responseType: "blob" });
  },
  downloadContractsExport: (request: ContractFilterExportRequest): AxiosPromise<Blob> => {
    return apiService.post(DOWNLOAD_CONTRACTS_EXPORT.url, request, {
      params: { report: request.report },
      responseType: "blob",
    });
  },
  downloadContractsVerificationExport: (): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_CONTRACTS_VERIFICATION_EXPORT.url, {
      responseType: "blob",
    });
  },
  downloadContractVehiclesExport: (request: EntityObject<ContractVehiclesExportRequest>): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_CONTRACT_VEHICLES_EXPORT.getParametrizedUrl(request.id), {
      params: request.object,
      paramsSerializer: { serialize: serializeParams },
      responseType: "blob",
    });
  },
  getContract: (request: EntityIdObject): AxiosPromise<Contract> => {
    return apiService.get(GET_CONTRACT.getParametrizedUrl(request.id));
  },
  getContractExternalView: (request: EntityIdObject): AxiosPromise<ContractExternalView> => {
    return apiService.get(GET_CONTRACT_EXTERNAL_VIEW.getParametrizedUrl(request.id));
  },
  filterContractCommissions: (request: EntityObject<PageRequest>): AxiosPromise<PageResult<ContractCommission>> => {
    return apiService.get(FILTER_CONTRACT_COMMISSIONS.getParametrizedUrl(request.id), { params: request.object });
  },
  createContract: (request: CreateUpdateContract): AxiosPromise<Contract> => {
    return apiService.post(CREATE_CONTRACT.url, request);
  },
  changeContractVerificationStatus: (
    request: EntityObject<ChangeContractVerificationStatus>,
  ): AxiosPromise<Contract> => {
    return apiService.post(CHANGE_CONTRACT_VERIFICATION_STATUS.getParametrizedUrl(request.id), request.object);
  },
  toggleVerificationExportInclusion: (request: EntityObject<OptimisticLockVersion>): AxiosPromise<Contract> => {
    return apiService.post(TOGGLE_VERIFICATION_EXPORT_INCLUSION.getParametrizedUrl(request.id), request.object);
  },
  updateContract: (request: EntityObject<CreateUpdateContract>): AxiosPromise<Contract> => {
    return apiService.put(UPDATE_CONTRACT.getParametrizedUrl(request.id), request.object);
  },
  updateContractEndDates: (request: EntityObject<UpdateContractEndDates>): AxiosPromise<Contract> => {
    return apiService.put(UPDATE_CONTRACT_END_DATES.getParametrizedUrl(request.id), request.object);
  },
  deleteContract: (request: EntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_CONTRACT.getParametrizedUrl(request.id));
  },
  updateComplaintNote: (request: EntityObject<UpdateContractComplaintNote>): AxiosPromise<Contract> => {
    return apiService.put(UPDATE_COMPLAINT_NOTE.getParametrizedUrl(request.id), request.object);
  },
  filterContractIncrements: (request: EntityObject<PageRequest>): AxiosPromise<PageResult<ContractIncrement>> => {
    return apiService.get(FILTER_CONTRACT_INCREMENTS.getParametrizedUrl(request.id), { params: request.object });
  },
  createContractIncrement: (request: EntityObject<CreateUpdateContractIncrement>): AxiosPromise<ContractIncrement> => {
    return apiService.post(CREATE_CONTRACT_INCREMENT.getParametrizedUrl(request.id), request.object);
  },
  updateContractIncrement: (
    request: TwoLevelEntityObject<CreateUpdateContractIncrement>,
  ): AxiosPromise<ContractIncrement> => {
    return apiService.put(UPDATE_CONTRACT_INCREMENT.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  deleteContractIncrement: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_CONTRACT_INCREMENT.getParametrizedUrl(request.id1, request.id2));
  },
  uploadContractAttachments: (request: EntityObject<FormData>): AxiosPromise<ContractAttachment[]> => {
    return apiService.post(UPLOAD_CONTRACT_ATTACHMENTS.getParametrizedUrl(request.id), request.object, {
      headers: { [HttpHeaders.CONTENT_TYPE]: HttpContentType.MULTIPART_FORM_DATA },
    });
  },
  updateContractAttachment: (
    request: TwoLevelEntityObject<UpdateContractAttachment>,
  ): AxiosPromise<ContractAttachment> => {
    return apiService.patch(UPDATE_CONTRACT_ATTACHMENT.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  downloadContractAttachment: (request: TwoLevelEntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_CONTRACT_ATTACHMENT.getParametrizedUrl(request.id1, request.id2), {
      responseType: "blob",
    });
  },
  downloadContractAttachmentsAsZip: (request: EntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_CONTRACT_ATTACHMENTS_AS_ZIP.getParametrizedUrl(request.id), {
      responseType: "blob",
    });
  },
  deleteContractAttachment: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_CONTRACT_ATTACHMENT.getParametrizedUrl(request.id1, request.id2));
  },
  downloadContractCommissionsExport: (
    request: EntityObject<{ exportFileType: ExportFileType }>,
  ): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_CONTRACT_COMMISSIONS_EXPORT.getParametrizedUrl(request.id), {
      params: request.object,
      paramsSerializer: { serialize: serializeParams },
      responseType: "blob",
    });
  },
  updateContractGainers: (request: EntityObject<UpdateContractGainers>): AxiosPromise<Contract> => {
    return apiService.put(UPDATE_CONTRACT_GAINERS.getParametrizedUrl(request.id), request.object);
  },
};

export const DEFAULT_FILTER_CONTRACTS_QUERY_PARAMS: Required<ContractFilterPageRequest> = {
  pageIndex: 0,
  pageSize: PageSizes.HUGE,
  keyword: "",
  report: ContractView.INTERNAL_GENERIC_REPORT,
  orderBy: [ContractOrderByColumn.CREATED_AT],
  orderDirections: [OrderDirection.DESC],
  institutionIds: [],
  productIds: [],
  signerIds: [],
  signerDirectSubordinateId: "",
  signerAllSubordinateId: "",
  signerDirectorateIds: [],
  managerIds: [],
  managerDirectSubordinateId: "",
  managerAllSubordinateId: "",
  managerDirectorateIds: [],
  gainerIds: [],
  gainerDirectSubordinateId: "",
  gainerAllSubordinateId: "",
  gainerDirectorateIds: [],
  affiliatePartnerIds: [],
  clientId: "",
  statuses: [],
  verificationStatuses: [],
  createdAtMin: "",
  createdAtMax: "",
  createdByKeyword: "",
  verifiedAtMin: "",
  verifiedAtMax: "",
  verifiedByKeyword: "",
  mediationReportSignDateMin: "",
  mediationReportSignDateMax: "",
  effectiveBeginningDateMin: "",
  effectiveBeginningDateMax: "",
  effectiveBeginningDateMonths: [],
  signDateMin: "",
  signDateMax: "",
  signDateMonths: [],
  origins: [],
  commissionBatchIds: [],
  effectiveEndDateOrLoanMaturityDateMin: "",
  effectiveEndDateOrLoanMaturityDateMax: "",
  cancellationDateMin: "",
  cancellationDateMax: "",
  transferredFromOtherBroker: false,
  transferredToOtherBrokerDateMin: "",
  transferredToOtherBrokerDateMax: "",
  annualPremiumOrApprovedAmountMin: 0,
  annualPremiumOrApprovedAmountMax: 0,
  investmentFeeTypes: [],
  commoditySavingsPlans: [],
  vehicleFirstRegistrationYearMin: 0,
  vehicleFirstRegistrationYearMax: 0,
  onlyIncludedInVerificationExport: false,
};

export const DEFAULT_FILTER_CONTRACTS_ANNIVERSARY_DATE_QUERY_PARAMS = {
  ...DEFAULT_FILTER_CONTRACTS_QUERY_PARAMS,
  report: ContractView.INTERNAL_ANNIVERSARY_DATE_REPORT,
  orderBy: [ContractOrderByColumn.LAST_CONTRACT_CANCELLATION_DATE],
  orderDirections: [OrderDirection.ASC],
};

export const DEFAULT_FILTER_CONTRACTS_FIXATION_ANNIVERSARY_QUERY_PARAMS = {
  ...DEFAULT_FILTER_CONTRACTS_QUERY_PARAMS,
  report: ContractView.INTERNAL_FIXATION_ANNIVERSARY_REPORT,
  orderBy: [ContractOrderByColumn.CONTACT_CLIENT_DATE],
  orderDirections: [OrderDirection.ASC],
};

export const DEFAULT_FILTER_CONTRACTS_PREDICTED_COMMISSIONS_QUERY_PARAMS = {
  ...DEFAULT_FILTER_CONTRACTS_QUERY_PARAMS,
  report: ContractView.INTERNAL_PREDICTED_COMMISSIONS_REPORT,
};

export const DEFAULT_FILTER_CONTRACTS_UNPAID_QUERY_PARAMS = {
  ...DEFAULT_FILTER_CONTRACTS_QUERY_PARAMS,
  report: ContractView.INTERNAL_UNPAID_REPORT,
};
